import { ServiceDefinitionWithItsRelatedCombos } from "@/Appointments/Models/Scheduling/ComboServiceModel";
import { ServiceDefinitionContext } from "@/Appointments/Models/Scheduling/CustomServiceModel";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ErrorMessageComponent } from "@/Common/Components/Errors/ErrorMessageComponent";
import { Combobox, ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { InformationLoadingComponent } from "@/Common/Components/Loader/InformationLoadingComponent";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { Guid } from "@/Common/Models/Guid";
import { ServiceComboTargetClientele } from "@/Common/Models/Services/Enums/ServiceComboTargetClientele";

interface IRulesetEditComponentProps {
    // New RuleSet Creation
    selectedComboServiceList: Immutable.List<ServiceDefinitionWithItsRelatedCombos>;
    employeeId?: Guid;
    rulesetId?: string; // Used to reset selection when context changes

    isSimpleRuleSet?: boolean;

    notifyParentCurrentStateChanged?: (rulesetEditComponentDTO: RulesetEditComponentDTO) => void;
}

class RulesetEditComponentDTO {
    public selectedComboServiceList: Immutable.List<ServiceDefinitionWithItsRelatedCombos>;

    public canSave: boolean;
}

class RulesetEditComponentState extends RulesetEditComponentDTO {
    public isLoading: boolean;
    public errors: string[];

    // Error Messages
    public showMinCalculationError: boolean;
    public showMaxCalculationError: boolean;
    public showRulesetUpdateError: boolean;

    // Red Border Controller for errors
    public displayItemErrors: boolean;
    public subitemInErrorId: string;
    public itemInErrorId: string;
    public itemInErrorServiceName: string;
}

class RulesetEditComponent extends BaseReactComponent<IRulesetEditComponentProps, RulesetEditComponentState> {
    private readonly targetClienteleList = this.getTargetClienteleOptions().toImmutable();

    private getTargetClienteleOptions(): ComboBoxDropDownItem[] {
        const options = [
            new ComboBoxDropDownItem(
                EnumHelper.getName(ServiceComboTargetClientele, ServiceComboTargetClientele.AssignedClientele),
                cacheStore.GetString("AssignedLabel")
            )
        ];

        if (cacheStore.ClinicSettings.EnableBookingAlgorithmSiteAndOrganizationTargetOptions) {
            if (cacheStore.SiteCache.length > 1)
                options.push(new ComboBoxDropDownItem(
                    EnumHelper.getName(ServiceComboTargetClientele, ServiceComboTargetClientele.Site),
                    cacheStore.GetString("SiteDLabel")
                ));

            if (cacheStore.ParentSiteCache.length > 1)
                options.push(new ComboBoxDropDownItem(
                    EnumHelper.getName(ServiceComboTargetClientele, ServiceComboTargetClientele.Organization),
                    cacheStore.GetString("OrganizationLabel")
                ));
        }

        options.push(
            new ComboBoxDropDownItem(
                EnumHelper.getName(ServiceComboTargetClientele, ServiceComboTargetClientele.Clinic),
                cacheStore.GetString("MyleEnvironnementLabel")
            )
        );
        options.push(
            new ComboBoxDropDownItem(
                EnumHelper.getName(ServiceComboTargetClientele, ServiceComboTargetClientele.Open),
                cacheStore.GetString("IsOpenLabel")
            )
        );

        return options;
    }

    constructor(props: IRulesetEditComponentProps) {
        super(props);
        this.state = this.getInitialState(props);

        this.props.notifyParentCurrentStateChanged?.(this.state);
    }

    public componentDidUpdate(prevProps: Readonly<IRulesetEditComponentProps>): void {
        if (this.props !== prevProps) {
            if (this.props.employeeId != prevProps.employeeId || this.props.rulesetId != prevProps.rulesetId || this.props.selectedComboServiceList != prevProps.selectedComboServiceList) {
                this.initState(this.props);
            }
        }
    }

    protected initState(nextProps: IRulesetEditComponentProps): void {
        this.mutateState(s => {
            s.isLoading = false;
            s.errors = [];
            s.selectedComboServiceList = [].toImmutable();

            s.showMinCalculationError = false;
            s.showMaxCalculationError = false;
            s.showRulesetUpdateError = false;
            s.displayItemErrors = false;
            s.subitemInErrorId = "";
            s.itemInErrorId = "";
            s.itemInErrorServiceName = "";
            s.canSave = true;

            s.selectedComboServiceList = [].toImmutable();
            if (nextProps.selectedComboServiceList != null && nextProps.selectedComboServiceList.count() > 0) {
                s.selectedComboServiceList = nextProps.selectedComboServiceList.toArray().map(x => ServiceDefinitionWithItsRelatedCombos.Clone(x)).sortByProperty(x => x.FullName).toImmutable();
                this.validateRuleset(s);
            }
            else {
                s.canSave = false;
            }
        });
    }

    public render(): JSX.Element {
        if (this.state.errors != null && this.state.errors.length > 0) {
            return <ErrorMessageComponent errors={this.state.errors} />;
        }
        else if (this.state.isLoading && (this.state.errors == null || this.state.errors.length == 0)) {
            return <InformationLoadingComponent smallVersion={true} customMessage={cacheStore.GetString("Synchronisation") + "..."} />;
        }
        else if (this.state.selectedComboServiceList == null || this.state.selectedComboServiceList.size == 0) {
            return <div />;
        }
        else {
            return (
                <div style={{ width: "800px" }}>
                    <div>
                        {this.state.showRulesetUpdateError === true && (
                            <div className="flex-container" style={{ justifyContent: "center" }}>
                                <label className="red-text">
                                    {cacheStore.GetString("InvalidRulesetModificationLabel")}
                                </label>
                            </div>
                        )}

                        {this.state.showMaxCalculationError === true && (
                            <div className="flex-container" style={{ justifyContent: "center" }}>
                                <label className="red-text">
                                    {cacheStore.GetString("MaxRuleSetErrorLabel")}
                                </label>
                            </div>
                        )}

                        {this.state.showMinCalculationError === true && (
                            <div className="flex-container" style={{ justifyContent: "center" }}>
                                <label className="red-text">
                                    {cacheStore.GetString("MinRuleSetErrorLabel")}
                                </label>
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="flex-container" style={{ borderBottom: "solid 1px lightgray" }}>
                            <div className="scheduling-col scheduling-col-service" />
                            {!this.props.isSimpleRuleSet && (
                                <div className="scheduling-col scheduling-col-min">
                                    <div>{cacheStore.GetString("MinCapitalLabel")}</div>
                                </div>
                            )}
                            {!this.props.isSimpleRuleSet && (
                                <div className="scheduling-col scheduling-col-max">
                                    <div>{cacheStore.GetString("MaxCapitalLabel")}</div>
                                </div>
                            )}
                            <div className="scheduling-col scheduling-col-online">
                                <div>{cacheStore.GetString("PortalLabel")}</div>
                            </div>
                            {cacheStore.ClinicSettings.EnableIsTeleconsultationCheckbox && (
                                <div className="scheduling-col scheduling-col-teleconsultation">
                                    <div>{cacheStore.GetString("TeleconsultationAbbrevLabel")}</div>
                                </div>
                            )}
                            <div className="scheduling-col scheduling-col-customerbase">
                                <div>{cacheStore.GetString("ClienteleLabel")}</div>
                            </div>
                            <div className="scheduling-col scheduling-col-delete" />
                        </div>
                        <div className="scrollable vertical-scroll-only">
                            {this.renderComboServices()}
                        </div>
                    </div>
                </div>
            );
        }
    }

    public renderComboServices(): JSX.Element[] {
        let comboServiceRenders: JSX.Element[] = [];
        this.state.selectedComboServiceList.forEach((item, i) => {
            var header: JSX.Element = null;
            var subItems: JSX.Element[] = [];

            var itemContainsOnlyService: boolean = (item.RelatedServiceDefinitionContexts.length == 1 && item.RelatedServiceDefinitionContexts[0].IsCombo == false);
            var onlyServiceSubItem = itemContainsOnlyService ? item.RelatedServiceDefinitionContexts[0] : null;

            if (item.RelatedServiceDefinitionContexts.length > 0) {
                var divCss: React.CSSProperties = (this.state.displayItemErrors && item.Id == this.state.itemInErrorId && this.state.subitemInErrorId == "ALL") ? divCss = { width: "39px", border: "solid 1px #ff0000" } : divCss = { width: "39px" };
                var divCss_minMax: React.CSSProperties = (this.state.displayItemErrors && item.Id == this.state.itemInErrorId && this.state.subitemInErrorId == "ALL") ? divCss = { width: "39px", border: "solid 1px #ff0000" } : divCss = { width: "39px", height: "37px" };

                header = (
                    <div key={"ruleSetItem_" + i} style={{ marginTop: "10px" }}>
                        {this.state.displayItemErrors === true && item.Id === this.state.itemInErrorId && (
                            <div className="flex-container" style={{ justifyContent: "center" }}>
                                <label className="red-text">
                                    <p>
                                        {cacheStore.GetString("MinGreaterThanMaxError1Label")}
                                        <strong>{this.state.itemInErrorServiceName}</strong>
                                    </p>
                                    <p>{cacheStore.GetString("MinMaxCommonErrorLabel")}</p>
                                </label>
                            </div>
                        )}

                        <div key="Group Row (Group Parameters)" className="flex-container">
                            <div className="scheduling-col scheduling-col-service" style={{ justifyContent: "flex-start" }}>
                                <strong className="underline">{item.NameWithDuration}</strong>
                            </div>

                            {!this.props.isSimpleRuleSet && (
                                <div className="scheduling-col scheduling-col-min">
                                    {/* use standaloneservice min/maxes when itemContainsOnlyService */}
                                    {itemContainsOnlyService && (
                                        <TextboxControl
                                            style={divCss_minMax}
                                            value={onlyServiceSubItem.Min && onlyServiceSubItem.Min > 0 ? onlyServiceSubItem.Min.toString() : ""}
                                            IsNumeric
                                            onChange={(value: number | string) =>
                                                this.mutateState(
                                                    s => this.mutateSelectedComboListSubItem(
                                                        s,
                                                        item.Id,
                                                        onlyServiceSubItem.Id,
                                                        subitem => {
                                                            const numberValue = typeof value === "number"
                                                                ? value
                                                                : parseFloat(value);

                                                            if (!isNaN(numberValue) && numberValue > 0)
                                                                subitem.Min = numberValue;
                                                            else
                                                                delete subitem.Min;
                                                        }
                                                    )
                                                )
                                            }
                                        />
                                    )}
                                </div>
                            )}

                            {!this.props.isSimpleRuleSet && (
                                <div className="scheduling-col scheduling-col-max">
                                    {/* use standaloneservice min/maxes when itemContainsOnlyService */}
                                    {itemContainsOnlyService && (
                                        <TextboxControl
                                            style={divCss_minMax}
                                            value={onlyServiceSubItem.Max ? onlyServiceSubItem.Max.toString() : ""}
                                            IsNumeric
                                            onChange={(value: number | string) =>
                                                this.mutateState(
                                                    s => this.mutateSelectedComboListSubItem(
                                                        s,
                                                        item.Id,
                                                        onlyServiceSubItem.Id,
                                                        subitem => {
                                                            const numberValue = typeof value === "number"
                                                                ? value
                                                                : parseFloat(value);

                                                            if (!isNaN(numberValue) && numberValue > 0)
                                                                subitem.Max = numberValue;
                                                            else
                                                                delete subitem.Max;
                                                        }
                                                    )
                                                )
                                            }
                                        />
                                    )}
                                    {/* user combogroup maxes when !itemContainsOnlyService */}
                                    {itemContainsOnlyService == false && (
                                        <TextboxControl
                                            style={divCss}
                                            value={item.GroupMax ? item.GroupMax.toString() : ""}
                                            IsNumeric
                                            onChange={(value: number | string) =>
                                                this.mutateState(
                                                    s => this.mutateSelectedComboList(
                                                        s,
                                                        item.Id,
                                                        item => {
                                                            const numberValue = typeof value === "number"
                                                                ? value
                                                                : parseFloat(value);

                                                            if (!isNaN(numberValue) && numberValue > 0)
                                                                item.GroupMax = numberValue;
                                                            else
                                                                delete item.GroupMax;
                                                        }
                                                    )
                                                )
                                            }
                                        />
                                    )}
                                </div>
                            )}

                            <div className="scheduling-col scheduling-col-online">
                                {itemContainsOnlyService && (
                                    <input
                                        id={"isOnline_" + onlyServiceSubItem.Id}
                                        type="checkbox"
                                        checked={onlyServiceSubItem.IsOnline}
                                        onChange={() =>
                                            this.mutateState(s => this.mutateSelectedComboListSubItem(s, item.Id, onlyServiceSubItem.Id, subItem => subItem.IsOnline = !subItem.IsOnline))
                                        }
                                    />
                                )}
                            </div>

                            {cacheStore.ClinicSettings.EnableIsTeleconsultationCheckbox && (
                                <div className="scheduling-col scheduling-col-teleconsultation">
                                    {itemContainsOnlyService && onlyServiceSubItem.IsTeleconsultation && (
                                        <span style={{ width: "20px" }}>
                                            {item.IsTeleconsultation &&
                                                <i className="fi-video col-lg-icon" />
                                            }
                                        </span>
                                    )}
                                </div>
                            )}

                            <div className="scheduling-col scheduling-col-customerbase">
                                {itemContainsOnlyService && (
                                    <Combobox
                                        HasEmptyItem={false}
                                        doInitialChangeOp={false}
                                        id="_clientComboBox"
                                        items={this.targetClienteleList}
                                        selectedCode={EnumHelper.getName(ServiceComboTargetClientele, onlyServiceSubItem?.TargetedClient)}
                                        onChange={value =>
                                            this.mutateState(s => this.mutateSelectedComboListSubItem(
                                                s,
                                                item.Id,
                                                onlyServiceSubItem.Id,
                                                subItem => subItem.TargetedClient = EnumHelper.getValue(ServiceComboTargetClientele, value)
                                            ))
                                        }
                                    />
                                )}
                            </div>

                            <div className="scheduling-col scheduling-col-delete">
                                {itemContainsOnlyService && (
                                    <span
                                        className="glyphicon glyphicon-remove red-text pointer"
                                        onClick={() =>
                                            this.mutateState(s => {
                                                s.selectedComboServiceList = this.state.selectedComboServiceList.filterImmutable(x => x.Id != item.Id);
                                                this.validateRuleset(s);
                                            })
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                );

                item.RelatedServiceDefinitionContexts.forEach((subItem: ServiceDefinitionContext, j: number) => {
                    if (itemContainsOnlyService == false) {
                        var subItemElement = (
                            <div key={"ruleSetSubItem_" + i + "_" + j} className="flex-container" style={{ paddingTop: "5px" }}>
                                <div className="scheduling-col scheduling-col-service" style={{ justifyContent: "flex-end" }}><label style={{ paddingTop: "7px" }}>{subItem.Name}</label></div>

                                <div className="scheduling-col scheduling-col-min">
                                    {subItem.IsCombo === false && (
                                        <TextboxControl
                                            style={divCss}
                                            value={subItem.Min && subItem.Min > 0 ? subItem.Min.toString() : ""}
                                            IsNumeric={true}
                                            onChange={(value: number) =>
                                                this.mutateState(s => this.mutateSelectedComboListSubItem(s, item.Id, subItem.Id, subitem => subitem.Min = Number(value)))
                                            }
                                        />
                                    )}
                                </div>
                                <div className="scheduling-col scheduling-col-max">
                                    <TextboxControl
                                        style={divCss}
                                        value={subItem.Max ? subItem.Max.toString() : ""}
                                        IsNumeric={true}
                                        onChange={(value: number) =>
                                            this.mutateState(s => this.mutateSelectedComboListSubItem(s, item.Id, subItem.Id, subitem => subitem.Max = Number(value)))
                                        }
                                    />
                                </div>

                                <div className="scheduling-col scheduling-col-online">
                                    <input
                                        id={"isOnline_" + subItem.Id}
                                        type="checkbox"
                                        checked={subItem.IsOnline}
                                        onChange={() =>
                                            this.mutateState(s => this.mutateSelectedComboListSubItem(s, item.Id, subItem.Id, subItem => subItem.IsOnline = !subItem.IsOnline))
                                        }
                                    />
                                </div>

                                <div className="scheduling-col scheduling-col-teleconsultation">
                                    {cacheStore.ClinicSettings.EnableIsTeleconsultationCheckbox && subItem.IsTeleconsultation && (
                                        <span style={{ width: "20px" }}>
                                            <i className="fi-video col-lg-icon" />
                                        </span>
                                    )}
                                </div>

                                <div className="scheduling-col scheduling-col-customerbase">
                                    <Combobox
                                        HasEmptyItem={false}
                                        doInitialChangeOp={false}
                                        id="_clientComboBox"
                                        items={this.targetClienteleList}
                                        selectedCode={EnumHelper.getName(ServiceComboTargetClientele, subItem.TargetedClient)}
                                        onChange={value =>
                                            this.mutateState(s => this.mutateSelectedComboListSubItem(
                                                s,
                                                item.Id,
                                                subItem.Id,
                                                subItem => subItem.TargetedClient = EnumHelper.getValue(ServiceComboTargetClientele, value)
                                            ))
                                        }
                                    />
                                </div>
                            </div>
                        );

                        subItems.push(subItemElement);
                    }
                });
            }

            comboServiceRenders.push(header);
            comboServiceRenders = comboServiceRenders.concat(subItems);
        });

        return comboServiceRenders;
    }

    public mutateState(
        mutation: (s: RulesetEditComponentState) => void,
        callback: () => any = null
    ): void {
        super.mutateState(
            mutation,
            () => {
                if (this.props.notifyParentCurrentStateChanged != null)
                    this.props.notifyParentCurrentStateChanged(this.state);

                if (callback != null)
                    return callback();
            }
        );
    }

    private getInitialState(currentProps: IRulesetEditComponentProps): RulesetEditComponentState {
        const initialState = new RulesetEditComponentState();

        initialState.isLoading = false;
        initialState.errors = [];
        initialState.selectedComboServiceList = [].toImmutable();

        initialState.showMinCalculationError = false;
        initialState.showMaxCalculationError = false;
        initialState.showRulesetUpdateError = false;
        initialState.displayItemErrors = false;
        initialState.subitemInErrorId = "";
        initialState.itemInErrorId = "";
        initialState.itemInErrorServiceName = "";
        initialState.canSave = true;

        initialState.selectedComboServiceList = [].toImmutable();
        if (currentProps.selectedComboServiceList != null && currentProps.selectedComboServiceList.count() > 0) {
            initialState.selectedComboServiceList = currentProps.selectedComboServiceList.toArray().map(x => ServiceDefinitionWithItsRelatedCombos.Clone(x)).sortByProperty(x => x.FullName).toImmutable();
            this.validateRuleset(initialState);
        }
        else {
            initialState.canSave = false;
        }

        return initialState;
    }

    private mutateSelectedComboListSubItem(s: RulesetEditComponentState, itemId: string, subItemId: string, mutation: (d: ServiceDefinitionContext) => any): void {
        if (this.state.selectedComboServiceList == null)
            return;

        this.mutateSelectedComboList(s, itemId, n => {
            n.RelatedServiceDefinitionContexts = n.RelatedServiceDefinitionContexts.map(d => {
                if (d.Id == subItemId)
                    mutation(d);
                return d;
            });
        });
    }

    private mutateSelectedComboList(s: RulesetEditComponentState, itemId: string, mutation: (d: ServiceDefinitionWithItsRelatedCombos) => any): void {
        if (this.state.selectedComboServiceList == null)
            return;

        s.selectedComboServiceList = this.state.selectedComboServiceList.toArray().map(n => {
            if (n.Id === itemId) {
                mutation(n);
            }
            return n;
        }).toImmutable();

        this.validateRuleset(s);
    }

    private validateRuleset(_state: RulesetEditComponentState): void {
        this.hideError(_state);
        _state.selectedComboServiceList = _state.selectedComboServiceList.toArray().map(n => {
            let subItemIdInError = ServiceDefinitionWithItsRelatedCombos.SubItemHasError(n);
            if (subItemIdInError != null) {
                this.showError(_state, n.Id, subItemIdInError[0] /* Id */, subItemIdInError[1] /* Name */);
            }
            return n;
        }).toImmutable();

        _state.canSave = !(_state.showMaxCalculationError || _state.showMinCalculationError || _state.showRulesetUpdateError || _state.displayItemErrors);
        _state.canSave = _state.canSave && _state.selectedComboServiceList != null && _state.selectedComboServiceList.count() > 0;
    }

    private hideError(_state: RulesetEditComponentState): void {
        _state.displayItemErrors = false;
        _state.subitemInErrorId = "";
        _state.itemInErrorId = "";
        _state.itemInErrorServiceName = "";
    }

    private showError(_state: RulesetEditComponentState, itemId: string, SubItemId: string, SubItemName: string): void {
        _state.displayItemErrors = true;
        _state.subitemInErrorId = SubItemId;
        _state.itemInErrorId = itemId;
        _state.itemInErrorServiceName = SubItemName;
    }
}

export { RulesetEditComponent, RulesetEditComponentDTO };
